import React, {useEffect, useMemo} from 'react'
import {useLocation} from 'react-router-dom'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Box,
    Button,
    Flex,
    Text,
    Modal,
    ModalHeader,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Stack,
    ModalFooter,
    ModalBody
} from '@chakra-ui/react'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import Link from '../link'
import RecommendedProducts from '../recommended-products'
import {LockIcon} from '../icons'
import Callout from '../content-asset'
import textStyles from '../../theme/foundations/textStyles'
import useMultiSite from '../../hooks/use-multi-site'
import {CART_HREF, CRO_LOCATION} from '../../constants'
import {useCroSlot} from '../cro'
import {getDisplayPrice} from '../../utils/product-utils'
import {useCookieConsent} from '../../hooks/use-cookie-consent'
import RecommendedAccessories from '../recommended-accessories'
import PayLater from '../pay-later'
import useEffectEvent from '../../hooks/use-effect-event'
import {useToast} from '../../hooks/use-toast'
import useProductItemsDetail from '../../hooks/useProductItemsDetail'
import useBreakpoints from '../../hooks/use-breakpoints'
import {useCurrentCustomer} from '../../hooks/use-current-customer'
import {useAddToCartModalContext} from '../../hooks/use-add-to-cart-modal'
import ModuleContainer from '../../leap/components/module-container'

/**
 * Visual feedback (a modal) for adding item to the cart.
 */
const AddToCartModal = () => {
    const {isOpen, onClose, data, onRecommendedProductClick, finalFocusRef} =
        useAddToCartModalContext()

    const {
        product,
        itemsAdded = [],
        count: quantity = itemsAdded.length,
        insufficientQuantity
    } = data || {}
    const intl = useIntl()
    const {
        data: basket = {},
        derivedData: {totalItems}
    } = useCurrentBasket()
    const {data: productItemsDetail} = useProductItemsDetail(basket)
    const size = useBreakpoints({lg: '2xl', xl: '4xl'})
    const {marketing: marketingCookieAccepted} = useCookieConsent()
    /**
     * [isCentered] is used to:
     * - Center the modal vertically on Desktop (Default - on mobile view modal is attached to bottom)
     * - [isCentered] = false ? Mobile : Desktop
     * - Hide (Cart items & Order details) on mobile view when recommendations are available
     */
    const isCentered = useBreakpoints({base: false, lg: true})
    const {site} = useMultiSite()
    const {currency, productSubTotal} = basket
    const location = useLocation()
    const isCart = /\/appcart$/.test(location?.pathname)
    const toast = useToast()
    const {isD2BCustomer, isCustomNetPriceEnabled} = useCurrentCustomer().data

    const cartProducts = useMemo(() => {
        return basket?.productItems
            ?.map((item) => productItemsDetail[item.productId])
            .filter(Boolean)
    }, [basket?.productItems, productItemsDetail])

    // show toast for D2B customers instead of the modal
    const onShowD2BToast = useEffectEvent(() => {
        toast({
            title: intl.formatMessage(
                {
                    defaultMessage:
                        '{quantity, plural, one {# item} other {# items}} have been added to your cart.',
                    id: 'global.info.d2b.added_to_cart'
                },
                {
                    quantity
                }
            ),
            description: insufficientQuantity
                ? intl.formatMessage({
                      defaultMessage:
                          'Some items are not available in desired quantity. Please review your cart.',
                      id: 'global.info.d2b.added_to_cart_insufficient_quantity'
                  })
                : null,
            status: 'success',
            duration: 8000,
            isClosable: true,
            position: 'top-right',
            // the variant is needed due to the different requirements
            // for the toast size - mobile full width, desktop max 25rem width
            variant: 'mobileFullWidthDesktopMax25rem'
        })
        // call close for the modal directly as we don't need to wait for anything more
        // the toast has separate manager to close it after the elapsed time
        // and user may add another product meanwhile
        onClose()
    })
    useEffect(() => {
        if (isOpen && isD2BCustomer) {
            onShowD2BToast()
        }
    }, [isOpen, isD2BCustomer, onShowD2BToast])

    useCroSlot(CRO_LOCATION.ADD_TO_CART_MODAL, isOpen)

    const addedSum = useMemo(() => {
        const displayPrice = getDisplayPrice(product, false, isCustomNetPriceEnabled)
        const price = displayPrice?.promotionalPrice || displayPrice?.price
        return price ? (price * data.count) / data.itemsAdded.length : null
    }, [product, isCustomNetPriceEnabled, data?.count, data?.itemsAdded?.length])

    if (!isOpen || isD2BCustomer) {
        return null
    }

    return (
        <Modal
            size={size}
            isOpen={isOpen}
            onClose={onClose}
            isCentered={isCentered}
            variant="cart"
            colorScheme="stokkeGray"
            finalFocusRef={finalFocusRef}
            returnFocusOnClose={!!finalFocusRef?.current}
        >
            <ModalOverlay />
            <ModalContent containerProps={{'data-testid': 'add-to-cart-modal'}}>
                <ModalHeader>
                    <Text role="status" textStyle="Regular 2XLarge">
                        {intl.formatMessage(
                            {
                                defaultMessage:
                                    '{quantity} {quantity, plural, one {item} other {items}} added to cart',
                                id: 'add_to_cart_modal.info.added_to_cart'
                            },
                            {quantity}
                        )}
                    </Text>
                </ModalHeader>
                {/* fill the space above footer on lg */}
                <Box bgColor="stokkeCore.white" />
                <ModalCloseButton />
                <ModalBody>
                    <ModuleContainer background="subtle" spacing="recommender">
                        {marketingCookieAccepted ? (
                            <RecommendedProducts
                                itemsPerSlide={{base: 1.7, s: 3}}
                                recommender={`recom_Basket_${site.id}`}
                                products={cartProducts}
                                shouldFetch={() => product?.id}
                                onRecommendedProductClick={onRecommendedProductClick}
                                title={
                                    <FormattedMessage
                                        defaultMessage="You Might Also Like"
                                        id="add_to_cart_modal.recommended_products.title.might_also_like"
                                    />
                                }
                            />
                        ) : (
                            <RecommendedAccessories
                                itemsPerSlide={{base: 1.7, s: 3}}
                                products={cartProducts}
                                shouldFetch={() => product?.id}
                                onRecommendedProductClick={onRecommendedProductClick}
                                title={
                                    <FormattedMessage
                                        defaultMessage="You Might Also Like"
                                        id="add_to_cart_modal.recommended_products.title.might_also_like"
                                    />
                                }
                            />
                        )}
                    </ModuleContainer>
                </ModalBody>
                <ModalFooter>
                    <Box order={-2}>
                        <Flex justifyContent="space-between" mb={4}>
                            <Text {...textStyles['Regular Medium']}>
                                {intl.formatMessage({
                                    defaultMessage: 'Just added',
                                    id: 'add_to_cart_modal.label.just_added'
                                })}
                            </Text>
                            <Text
                                alignSelf="flex-end"
                                {...textStyles['Regular Medium']}
                                data-testid="product-price"
                            >
                                {addedSum &&
                                    intl.formatNumber(addedSum, {
                                        style: 'currency',
                                        currency: product.currency || currency
                                    })}
                            </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                            <Text {...textStyles['Regular Medium']}>
                                {intl.formatMessage(
                                    {
                                        defaultMessage:
                                            'Cart subtotal ({itemAccumulatedCount, plural, =0 {0 items} one {# item} other {# items}})',
                                        id: 'add_to_cart_modal.label.cart_subtotal'
                                    },
                                    {itemAccumulatedCount: totalItems}
                                )}
                            </Text>
                            <Text
                                alignSelf="flex-end"
                                {...textStyles['Regular Medium']}
                                data-testid="cart-subtotal"
                            >
                                {productSubTotal &&
                                    intl.formatNumber(productSubTotal, {
                                        style: 'currency',
                                        currency: currency
                                    })}
                            </Text>
                        </Flex>
                    </Box>

                    <PayLater
                        amount={productSubTotal}
                        currency={currency}
                        paypalPlacement="cart"
                        klarnaPlacement="credit-promotion-badge"
                    />

                    <Stack order={{base: 0, lg: -1}} spacing="4">
                        <Button
                            as={Link}
                            to={CART_HREF}
                            onClick={() => isCart && onClose()}
                            width="100%"
                            variant="outline"
                            size="lg"
                            {...textStyles['Regular Medium']}
                        >
                            {intl.formatMessage({
                                defaultMessage: 'View Cart',
                                id: 'add_to_cart_modal.link.view_cart'
                            })}
                        </Button>

                        <Button
                            as={Link}
                            to="/appcheckout"
                            width="100%"
                            variant="placeOrder"
                            size="lg"
                            rightIcon={
                                <LockIcon
                                    role="img"
                                    aria-label={intl.formatMessage({
                                        defaultMessage: 'Secure Checkout',
                                        id: 'global.secure_checkout'
                                    })}
                                    aria-hidden={false}
                                />
                            }
                            {...textStyles['Regular Medium']}
                        >
                            {intl.formatMessage({
                                defaultMessage: 'Proceed to Checkout',
                                id: 'add_to_cart_modal.link.checkout'
                            })}
                        </Button>
                    </Stack>

                    <Callout id="pwa-addtocart-usp" />
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

export default AddToCartModal
